import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./sidenav.css";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { clearAuth, stopImpersonation } from "../../store/slices/authSlice";
import { useUserPermissions } from "../../hooks/useUserPermissions";
import {
  LayoutDashboard,
  ClipboardList,
  Mail,
  TrendingUp,
  CreditCard,
  Receipt,
  Package,
  Shield,
  BarChart3,
  Users,
  Tag,
  Building2,
  Bell,
  FileText,
  CalendarDays,
  Settings,
  Undo2,
  LogOut,
  ChevronDown,
  MessageSquare,
  Menu as MenuIcon,
  X,
  type LucideIcon,
} from "lucide-react";

interface NavItem {
  icon: LucideIcon;
  path: string;
  label: string;
  alt: string;
  className?: string;
  subItems?: NavItem[];
  /**
   * Extra route prefixes this item owns, for detail views that don't live
   * under `path`. Highlighting only - navigation still goes to `path`.
   */
  matchPaths?: string[];
}

/*
 * One reusable style for every nav row, so the sidebar cannot drift again.
 * The panel is near-black, so the active state is a primary-700 fill and the
 * hover state a translucent white wash (a real surface, not just a text
 * colour change).
 */
const navRowBase =
  // The sidebar panel is near-black, so focus uses a light primary border to
  // stay visible against it.
  "border border-transparent group flex w-full items-center gap-3 rounded-xl text-left transition-colors cursor-pointer select-none focus:outline-none focus-visible:border-primary-400";

const navRowClass = (active: boolean) =>
  `${navRowBase} px-3 py-2.5 text-sm ${
    active
      ? "bg-primary-700 text-white font-medium"
      : "text-neutral-300 hover:bg-white/10 hover:text-white"
  }`;

// Sub-items sit indented under their parent and read one step quieter.
const subNavRowClass = (active: boolean) =>
  `${navRowBase} pl-11 pr-3 py-2 text-[13px] ${
    active
      ? "bg-primary-700 text-white font-medium"
      : "text-neutral-400 hover:bg-white/10 hover:text-white"
  }`;

const NAV_ICON_PROPS = { className: "w-[18px] h-[18px] shrink-0", strokeWidth: 1.75 };

/*
 * A nav item is active on its own route and on routes nested beneath it, so
 * detail views keep their section highlighted (/analytics/detail/:id lights up
 * "Analytics", /roster/artist-details/:id lights up "Roster").
 *
 * The trailing slash matters: it compares whole path segments, so "/dspp" does
 * not light up for "/dsp-organizations".
 */
const matchesPath = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * True when the route belongs to this nav item - either nested under its path,
 * or listed in `matchPaths` for detail views that live somewhere else entirely
 * (e.g. /artist/analytics-details/:id belongs to the Analytics section).
 */
const isItemActive = (pathname: string, item: NavItem) =>
  matchesPath(pathname, item.path) ||
  !!item.matchPaths?.some((p) => matchesPath(pathname, p));

export const SideNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
   const { email: emailPermission } = useUserPermissions();
   const hasEmailAccess = !!emailPermission && emailPermission.limit !== 0;

  const toggleMenu = () => setIsOpen(!isOpen);

  const toggleDropdown = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const navItems: NavItem[] = [
    {
      icon: LayoutDashboard,
      path: "/dashboard",
      label: user?.role === "dsp" ? "DSP Dashboard" : "Dashboard",
      alt: user?.role === "dsp" ? "DSP Dashboard" : "Dashboard",
    },
  ];

  // Add navigation items based on role
  if (user?.role !== "dsp") {
    navItems.push(
      {
        icon: ClipboardList,
        path: "/past-submissions",
        label: "Past Submissions",
        alt: "Past Submissions",
        className: "nav-past-submissions",
        // A pitch's submission and edit views are reached from here, so keep this
        // section highlighted while viewing or editing one.
        matchPaths: ["/pitch/submission", "/pitch/edit"],
      },
      ...(hasEmailAccess || user?.role ==="admin" ? [{
        icon: Mail,
        path: "/email",
        label: "Email",
        alt: "Email",
      }] : []),
      {
        icon: TrendingUp,
        path: "/analytics",
        label: "Analytics",
        alt: "Analytics",
        // Artist analytics detail lives outside /analytics, so claim it here.
        matchPaths: ["/artist/analytics-details"],
      }
    );
  } else {
    navItems.push({
      icon: TrendingUp,
      path: "/analytics",
      label: "Analytics",
      alt: "Analytics",
      matchPaths: ["/artist/analytics-details"],
    });
  }



  // Insert "Subscription" dropdown after "Past Submissions" (index 1)
  if (user?.role === "admin") {
    navItems.splice(2, 0, {
      icon: CreditCard,
      path: "/transactions",
      label: "Subscription",
      alt: "Subscription",
      subItems: [
        {
          icon: Receipt,
          path: "/transactions",
          label: "Transaction",
          alt: "Transaction",
        },
        {
          icon: Package,
          path: "/custom-subscription",
          label: "Custom Subscription",
          alt: "Custom Subscription",
        },
        {
          icon: Shield,
          path: "/exclusive-permissions",
          label: "Exclusive Permissions",
          alt: "Exclusive Permissions",
        },
      ],
    });

    navItems.splice(3, 0, {
      icon: BarChart3,
      path: "/admin/analytics",
      label: "Admin Analytics",
      alt: "Admin Analytics",
    });

    navItems.splice(4, 0, {
      icon: Users,
      path: "/master-roster",
      label: "Master Roster",
      alt: "Master Roster",
    });

    navItems.push({
      icon: Tag,
      path: "/labels",
      label: "Labels",
      alt: "Labels",
    });

    navItems.push({
      icon: Building2,
      path: "/dsp-organizations",
      label: "DSP Organizations",
      alt: "DSP Organizations",
    });

    navItems.push({
      icon: Bell,
      path: "/app-updates",
      label: "App Updates",
      alt: "App Updates",
    });
  }
  if (user?.role === "basic") {
    navItems.push({
      icon: FileText,
      path: "/pitch/create",
      label: "Pitch Form",
      alt: "Pitch Form",
    },
    {
      icon: CalendarDays,
      path: "/roster",
      label: "Roster",
      alt: "Roster",
    })

  }

  navItems.splice(navItems.length, 0, {
      icon: Settings,
      path: "/settings",
      label: "Settings",
      alt: "Settings",
    },)

  const handleLogout = () => {
    dispatch(clearAuth());
    navigate("/auth/login");
  };

  const handleStopImpersonation = () => {
    dispatch(stopImpersonation());
    navigate("/dashboard");
  };

  return (
    <>
      {/* Mobile Top Bar */}
      <div className="mobile-header">
        <button
          type="button"
          className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          onClick={() => navigate("/dashboard")}
          aria-label="Go to dashboard"
        >
          <img
            src="/images/logo.png"
            alt="Cadence IO"
            className="mobile-logo"
          />
        </button>
        <button
          type="button"
          className="p-2 transition-colors rounded-lg text-neutral-300 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          onClick={toggleMenu}
          aria-label={isOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={isOpen}
        >
          {isOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
        </button>
      </div>

      {/* Tap-outside-to-close. Mobile only - hidden at >=769px in CSS. */}
      <button
        type="button"
        className={`nav-scrim ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(false)}
        tabIndex={isOpen ? 0 : -1}
        aria-label="Close navigation"
      />

      {/* Side Navigation */}
      <div className={`side-nav ${isOpen ? "open" : ""}`}>
        <img onClick={() => { navigate('/dashboard'); setIsOpen(false); }}
         src="/images/logo.png" alt="Cadence IO" className="cursor-pointer logo" />
        <nav className="nav-list">
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = !item.subItems && isItemActive(location.pathname, item);
            // Presentational only: a parent whose child route is open reads as
            // engaged without changing which path is considered "active".
            const hasActiveChild = !!item.subItems?.some(
              (sub) => isItemActive(location.pathname, sub)
            );
            const isExpanded = openDropdown === item.label;

            return (
            <div
              key={index}
              className={item.className || ""}
            >
              <div
                className={
                  item.subItems
                    ? `${navRowClass(false)} ${
                        hasActiveChild || isExpanded ? "bg-white/5 text-white" : ""
                      }`
                    : navRowClass(isActive)
                }
                onClick={() => {
                  if (item.subItems) {
                    toggleDropdown(item.label);
                  } else {
                    navigate(item.path);
                    setIsOpen(false); // Close on mobile
                  }
                }}
                role="button"
                tabIndex={0}
                aria-current={isActive ? "page" : undefined}
                aria-expanded={item.subItems ? isExpanded : undefined}
              >
                <Icon {...NAV_ICON_PROPS} />
                <span className="flex-1 truncate">{item.label}</span>
                {item.subItems && (
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                )}
              </div>
              {item.subItems && (
                <div
                  className={`dropdown-menu ${isExpanded ? "open" : ""}`}
                >
                  {item.subItems.map((subItem, subIndex) => {
                    const SubIcon = subItem.icon;
                    const subActive = isItemActive(location.pathname, subItem);
                    return (
                    <div
                      key={subIndex}
                      className={subNavRowClass(subActive)}
                      onClick={() => {
                        navigate(subItem.path);
                        setIsOpen(false); // Close on mobile
                      }}
                      role="button"
                      tabIndex={0}
                      aria-current={subActive ? "page" : undefined}
                    >
                      <SubIcon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                      <span className="truncate">{subItem.label}</span>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}
          {/*
            Bottom group. `mt-auto` lives on this wrapper rather than on the
            feedback card: the card is conditional, and when it was the only
            element carrying it, hiding it left Logout sitting directly under the
            nav items instead of pinned to the bottom.
          */}
          <div className="mt-auto">
          {/* Beta Feedback opens an external form, so it's an anchor rather than a route.
              Shown as a highlighted card (matching the old Settings-page treatment) pinned to
              the bottom, above the hairline and Logout.

              Not shown to admins: it's a beta-feedback channel for the people using
              the product, and admins are the ones receiving that feedback. */}
          {process.env.REACT_APP_BETA_FEEDBACK_URL && user?.role !== "admin" && (
            <a
              href={process.env.REACT_APP_BETA_FEEDBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-2.5 py-3 no-underline transition-colors border rounded-xl cursor-pointer border-primary-700/40 bg-primary-700/15 hover:bg-primary-700/25 focus:outline-none focus-visible:border-primary-400"
              onClick={() => setIsOpen(false)}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-[18px] h-[18px] shrink-0 text-primary-300" strokeWidth={1.75} />
                <p className="text-sm font-semibold text-white">Beta Feedback</p>
              </div>
              <p className="mt-1 text-sm text-neutral-400">
                Help us improve! Share your thoughts.
              </p>
            </a>
          )}

          {/* Pinned to the bottom, separated by a hairline, muted until hover. */}
          <div className="pt-2 mt-3 border-t border-white/10">
            {user?.impersonated ? (
              <div
                className={`${navRowBase} px-3 py-2.5 text-sm text-neutral-400 hover:bg-white/10 hover:text-white`}
                onClick={handleStopImpersonation}
                role="button"
                tabIndex={0}
              >
                <Undo2 {...NAV_ICON_PROPS} />
                <span className="truncate">Stop Impersonating</span>
              </div>
            ) : (
              <div
                className={`${navRowBase} px-3 py-2.5 text-sm text-neutral-400 hover:bg-white/10 hover:text-white`}
                onClick={handleLogout}
                role="button"
                tabIndex={0}
              >
                <LogOut {...NAV_ICON_PROPS} />
                <span className="truncate">Logout</span>
              </div>
            )}
          </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default SideNav;
